import { useCallback, useEffect, useRef, useState } from "react"

interface useImageGalleryScrollOptions {
    isMobile?: boolean
    mobileBreakpoint?: number
}

function isHorizontalGallery(isMobile: boolean | undefined, mobileBreakpoint: number) {
    return isMobile ?? window.innerWidth < mobileBreakpoint
}

function getFirstSlide(container: HTMLElement) {
    const innerWrapper = container.firstElementChild as HTMLElement | null
    return innerWrapper?.firstElementChild as HTMLElement | null
}

export function useImageGalleryScroll(options: useImageGalleryScrollOptions = {}) {
    const { isMobile, mobileBreakpoint = 968 } = options
    const [currentIndex, setCurrentIndex] = useState(0)
    const containerRef = useRef<HTMLDivElement>(null)
    const requestAnimationframeRef = useRef<number | undefined>(undefined)

    useEffect(() => {
        const container = containerRef.current
        if (!container) return

        const handleScroll = () => {
            if (requestAnimationframeRef.current) {
                cancelAnimationFrame(requestAnimationframeRef.current)
            }
            requestAnimationframeRef.current = requestAnimationFrame(() => {
                if (isHorizontalGallery(isMobile, mobileBreakpoint)) {
                    const imageWidth = container.clientWidth
                    if (imageWidth === 0) return
                    setCurrentIndex(Math.round(container.scrollLeft / imageWidth))
                    return
                }

                const firstSlide = getFirstSlide(container)
                if (!firstSlide || firstSlide.offsetHeight === 0) return
                setCurrentIndex(Math.round(container.scrollTop / firstSlide.offsetHeight))
            })
        }

        container.addEventListener("scroll", handleScroll, { passive: true })
        return () => {
            container.removeEventListener("scroll", handleScroll)
            if (requestAnimationframeRef.current) {
                cancelAnimationFrame(requestAnimationframeRef.current)
            }
        }
    }, [isMobile, mobileBreakpoint])

    const scrollToIndex = useCallback(
        (index: number) => {
            const container = containerRef.current
            if (!container || index < 0) return

            const prefersReducedMotion = window.matchMedia(
                "(prefers-reduced-motion: reduce)"
            ).matches
            const behavior = prefersReducedMotion ? "auto" : "smooth"

            const slides = container.querySelectorAll<HTMLElement>("[data-gallery-slide]")
            const slide = slides[index]
            if (slide) {
                slide.scrollIntoView({
                    behavior,
                    block: "nearest",
                    inline: "nearest"
                })
                return
            }

            if (isHorizontalGallery(isMobile, mobileBreakpoint)) {
                container.scrollTo({
                    left: index * container.clientWidth,
                    behavior
                })
                return
            }

            const firstSlide = getFirstSlide(container)
            if (!firstSlide) return
            container.scrollTo({
                top: index * firstSlide.offsetHeight,
                behavior
            })
        },
        [isMobile, mobileBreakpoint]
    )

    return {
        currentIndex,
        setCurrentIndex,
        containerRef,
        scrollToIndex
    }
}
